import { useCallback, useState, useEffect } from 'react';
import { Input, AutoComplete, Space } from 'antd';
import { HistoryOutlined } from '@ant-design/icons';

import './SearchBox.less';
import SearchBoxNamespaceSelectContainer from '../../containers/SearchBoxNamespaceSelectContainer';
import { readHistory, persistHistory } from './searchHistory';
import LRASet from './LRASet';
import IconText from '../IconText';

let HISTORY_BY_NAMESPACE = {};
readHistory((history: History) => {
  HISTORY_BY_NAMESPACE = history;
});

const SearchBox = ({
  value,
  placeholder,
  onSearch,
  namespace,
  className,
}: {
  value?: string;
  placeholder?: string;
  onSearch: Function;
  namespace: string;
  className?: string;
}) => {
  const [inputValue, setInputValue] = useState(value);
  const [autoCompleteOptions, setAutoCompleteOptions] = useState<
    { value: any; label: React.JSX.Element }[]
  >([]);
  const [shouldSearch, setShouldSearch] = useState(false);

  // always override inputValue when `value` prop is changed
  useEffect(() => {
    // set empty string if `value` is null to force clearing text value of internal Input field.
    const valueOrEmpty = value != null ? value : '';
    setInputValue(valueOrEmpty);
  }, [value]);

  const onInputChange = useCallback(
    (event: React.ChangeEvent<HTMLInputElement>) => {
      const newValue = event.target.value;
      setInputValue(newValue);
      const namespaceHistory: LRASet =
        HISTORY_BY_NAMESPACE[namespace as keyof typeof HISTORY_BY_NAMESPACE];
      if (
        newValue &&
        HISTORY_BY_NAMESPACE[namespace as keyof typeof HISTORY_BY_NAMESPACE]
      ) {
        const options: { value: any; label: React.JSX.Element }[] = [
          ...new Set<string>(
            namespaceHistory.filter(
              (searchQuery: string) =>
                searchQuery !== newValue && searchQuery.includes(newValue)
            )
          ),
        ].map((searchQuery: string) => ({
          value: searchQuery,
          label: <IconText icon={<HistoryOutlined />} text={searchQuery} />,
        }));
        setAutoCompleteOptions(
          options.length
            ? [{ value: newValue, label: <span>{newValue}</span> }, ...options]
            : []
        );
      } else {
        setAutoCompleteOptions([]);
      }
    },
    [namespace]
  );
  const onInputSearch = useCallback(() => {
    setShouldSearch(true);
  }, []);

  const onAutoCompleteSelect = useCallback((selectedValue: string) => {
    setInputValue(selectedValue);
    setShouldSearch(true);
  }, []);

  useEffect(
    () => {
      // FIXME: do it without an effect.
      // HACK: to perform a search only once after `Input.Search.onSearch` or `AutoComplete.onSelect`
      // since `onSelect` is called after `onSearch`, we can't do this `onSearch`.
      if (shouldSearch) {
        onSearch(namespace, inputValue);

        if (inputValue) {
          (
            HISTORY_BY_NAMESPACE[
              namespace as keyof typeof HISTORY_BY_NAMESPACE
            ] as LRASet
          ).add(inputValue.trim());
          persistHistory(HISTORY_BY_NAMESPACE);
        }
        setShouldSearch(false);
        setAutoCompleteOptions([]);
      }
    },
    [shouldSearch] // eslint-disable-line react-hooks/exhaustive-deps
  );

  return (
    // HACK: autocomplete select and scope select interferes with each other
    // when search scope is rendered using `Input.addonBefore` prop, inside autocomplete
    // that's why it's pulled out, and classNames are set manually to make it look like an addon
    <>
      <Space.Compact
        data-testid="searchbox"
        className={className}
        block
        style={{ alignItems: 'center' }}
      >
        <span className="ant-input-group-addon">
          <SearchBoxNamespaceSelectContainer />
        </span>
        <AutoComplete
          autoFocus
          classNames={{ popup: { root: 'header-dropdown' } }}
          className="autocomplete"
          value={inputValue}
          popupMatchSelectWidth
          options={autoCompleteOptions}
          onSelect={onAutoCompleteSelect}
          style={{ width: '100%' }}
        >
          <Input.Search
            data-testid="search-box-input"
            placeholder={placeholder}
            size="large"
            enterButton
            onChange={onInputChange}
            onSearch={onInputSearch}
          />
        </AutoComplete>
      </Space.Compact>
    </>
  );
};

export default SearchBox;
